interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (pagina: number) => void;
  onLimitChange?: (limite: number) => void;
  label?: string;
}

/** Gera a sequência de páginas com ellipsis (1 ... atual-1 atual atual+1 ... total). */
function gerarPaginas(atual: number, total: number): (number | '...')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const conjunto = new Set<number>([1, total, atual - 1, atual, atual + 1]);
  const ordenadas = [...conjunto].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const resultado: (number | '...')[] = [];
  let anterior = 0;
  for (const pagina of ordenadas) {
    if (pagina - anterior > 1) resultado.push('...');
    resultado.push(pagina);
    anterior = pagina;
  }
  return resultado;
}

/**
 * Botões de paginação numerados (padrão obrigatório do projeto — sem variantes).
 * Página atual destacada + chevrons + seletor de limite opcional.
 */
export default function PaginationControls({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onLimitChange,
  label = 'itens',
}: PaginationControlsProps) {
  if (totalPages <= 1 && totalItems <= itemsPerPage) {
    return (
      <div className="flex items-center justify-between gap-2 mt-4 text-sm text-gray-400">
        <span>
          {totalItems} {label}
        </span>
        {onLimitChange && (
          <select
            value={itemsPerPage}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-sm text-gray-300"
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} por página
              </option>
            ))}
          </select>
        )}
      </div>
    );
  }

  const paginas = gerarPaginas(currentPage, totalPages);
  const inicio = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const fim = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
      <span className="text-sm text-gray-400">
        {inicio}–{fim} de {totalItems} {label}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="px-2.5 py-1.5 rounded-lg btn-secondary text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Página anterior"
        >
          ‹
        </button>
        {paginas.map((pagina, i) =>
          pagina === '...' ? (
            <span key={`e${i}`} className="px-1 text-gray-500">
              …
            </span>
          ) : (
            <button
              key={pagina}
              onClick={() => onPageChange(pagina)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                pagina === currentPage
                  ? 'bg-primary-600 text-white'
                  : 'btn-secondary text-gray-200'
              }`}
            >
              {pagina}
            </button>
          ),
        )}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="px-2.5 py-1.5 rounded-lg btn-secondary text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          aria-label="Próxima página"
        >
          ›
        </button>
        {onLimitChange && (
          <select
            value={itemsPerPage}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="ml-2 bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-gray-300"
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}/pág
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}